import * as React from 'react';
import debounce from 'lodash/debounce';

import { FormField } from 'components/form';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import DeliverableTag from 'modules/deliverables/components/deliverable-tag/DeliverableTag';

import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./deliverables-metrics-form.i18n.json'));

require('./deliverables-metrics-form.scss');

import type {
  AdminDeliverable,
  Data,
  DeliverableTag as DeliverableTagType,
  DeliverableType,
  MixedSelectDropdownItem
} from 'types';

type Props = {
  deliverable: AdminDeliverable,
  deliverableTypes: Array<DeliverableType>,
  onTagUpdate: (tag?: DeliverableTagType | null) => void,
  onTypeUpdate: (
    deliverableType: DeliverableType,
    hasTaskTypeChanged: boolean,
    tag: DeliverableTagType | null | undefined
  ) => void
};

type State = {
  tag: DeliverableTagType | undefined | null
};

class DeliverablesMetricsFormHeader extends React.PureComponent<Props, State> {
  state = {
    tag: this.props.deliverable.tag
  };

  debouncedTagUpdate = debounce(this.props.onTagUpdate, 500);

  private handleTagUpdate = ({ tag }: { tag: DeliverableTagType | null | undefined }): void => {
    this.setState({ tag });
    this.debouncedTagUpdate(tag);
  };

  private handleTypeChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ) => {
    const { deliverable, deliverableTypes, onTypeUpdate } = this.props;
    const value = selectedItem?.identifier || deliverableTypes[0];
    const deliverableType = deliverable.deliverableType;

    if (deliverableType === value) {
      return;
    }

    const taskType = value.split('_').pop() || '';

    onTypeUpdate(value, !deliverableType.includes(taskType), this.state.tag);
  };

  private getDeliverableTypesDropdownData(): Array<MixedSelectDropdownItem> {
    const { deliverable, deliverableTypes } = this.props;

    return deliverableTypes.map(type => ({
      identifier: type,
      isDisabled: false,
      isSelected: deliverable.deliverableType === type,
      label: __t(`deliverables_metrics_form.types.${type}`),
      value: type
    }));
  }

  private renderTypeChooser(): React.ReactNode {
    const { deliverable } = this.props;

    return (
      <div className="deliverables-metrics-form__form-section">
        <FormField
          label={__t('deliverables_metrics_form.label_type_of_task')}
          labelType="highlighted"
          labelSize="collapsed"
          showValidationIcon={false}
          direction="column"
          fontSize="small"
        >
          <MixedSelectDropdown
            key={`deliverables-metrics-form__dropdown--${deliverable.deliverableType}`}
            name="deliverables-metrics-form__dropdown"
            items={this.getDeliverableTypesDropdownData()}
            withSelectedInList
            className="deliverables-metrics-form__dropdown"
            onItemsToggle={this.handleTypeChange}
          />
        </FormField>
      </div>
    );
  }

  private renderTagForm = (): JSX.Element => (
    <div className="deliverables-metrics-form__form-section">
      <DeliverableTag
        deliverableTag={this.props.deliverable.tag}
        onEdit={this.handleTagUpdate}
      />
    </div>
  );

  render() {
    return (
      <React.Fragment>
        <div className="deliverables-metrics-form__title">
          {__t('deliverables_metrics_form.title')}
        </div>
        {this.renderTypeChooser()}
        {this.renderTagForm()}
        <div className="deliverables-metrics-form__title">
          {__t('deliverables_metrics_form.label_analytics')}
        </div>
      </React.Fragment>
    );
  }
}

export default DeliverablesMetricsFormHeader;
